import { CommonModule } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SearchResultsService } from 'src/app/pages/start-page/services/search-results.service';
import { TooltipDirective } from '../../directives/tooltip.directive';
import { AppInterface, ClickOnAppBehavior, DisplayType } from '../../interfaces/app.interface';
import { NavigationItemsService } from '../../services/navigation-items-service/navigation-items.service';
import { ScrollService } from '../../services/scroll-service/scroll.service';
import { UrlService } from '../../services/url-service/url.service';
import { AddRemoveAppButtonComponent } from './add-remove-button/add-remove-app-button.component';
import { IconComponent } from './app-icon/icon.component';

@Component({
  selector: 'app',
  imports: [
    CommonModule,
    IconComponent,
    AddRemoveAppButtonComponent,
    FormsModule,
    TooltipDirective,
  ],
  templateUrl: './application.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./application.component.scss'],
})
export class ApplicationComponent implements AfterContentChecked {
  @Input() app!: AppInterface;
  @Input() displayType: DisplayType = DisplayType.SMALL;
  @Input() showAddRemoveButton: boolean = true;
  @Input() clickOnAppBehavior: ClickOnAppBehavior = ClickOnAppBehavior.OPEN_APP_DETAILS;
  @Input() isAppFinderPage: boolean = false;
  @Input() recommendedSectionIdentification: string = '';
  @Input() isFromDetailPage: boolean = false;
  @Input() isUnpublished: boolean = false;

  isClicked = output<void>();
  @ViewChild('titleElement') titleElement!: ElementRef;

  protected readonly DisplayType = DisplayType;
  protected readonly ClickOnAppBehavior = ClickOnAppBehavior;

  constructor(
    private changeDetectionRef: ChangeDetectorRef,
    private navigationItems: NavigationItemsService,
    private router: Router,
    private urlService: UrlService,
    private scrollService: ScrollService,
    private searchResultService: SearchResultsService,
  ) {}

  ngAfterContentChecked(): void {
    this.changeDetectionRef.detectChanges();
  }

  openAppURL(): void {
    this.urlService.openURL(this.app.appURL);
  }

  async openAppDetail(openInNewTab: boolean = false): Promise<void> {
    this.scrollService.savePosition(this.router.url);
    let appDetailUrl = this.router.serializeUrl(
      this.router.createUrlTree([
        this.navigationItems.getAppDetailPage().routerLink,
        this.app.appId,
      ]),
    );

    if (this.app.isBookmark) {
      appDetailUrl = this.app.bookmarkURL ?? '';
    }

    if (openInNewTab) {
      window.open(appDetailUrl, '_blank');
    } else {
      await this.router.navigate([
        this.navigationItems.getAppDetailPage().routerLink,
        this.app.appId,
      ]);
      window.scroll(0, 0);
    }
  }

  stopPropagation(event: MouseEvent) {
    event.stopPropagation();
  }

  protected onAppClicked() {
    this.isClicked.emit();

    const startPageSearchResultBehavior = this.searchResultService.shouldOpenSearch.getValue();
    if (startPageSearchResultBehavior || !startPageSearchResultBehavior) {
      switch (this.clickOnAppBehavior) {
        case ClickOnAppBehavior.OPEN_APP_DETAILS:
          this.openAppDetail(false).then();
          break;
        case ClickOnAppBehavior.OPEN_APP_BY_URL:
          this.openAppURL();
          break;
        case ClickOnAppBehavior.OPEN_APP_DETAILS_NEW_TAB:
          this.openAppDetail(true).then();
          break;
        case ClickOnAppBehavior.NO_BEHAVIOR:
          return;
        default:
          return;
      }
    }
  }
}
